import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, IconButton, type SxProps, type Theme, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { formatWeekRangeWithMonth } from "../../utils/dateFormatting";
import type { RecurringShift, TimeOffRequest } from "../../utils/models";
import { LocationCellProvider } from "../MasterSchedule/v2/Cells/LocationCellContext";
import { getGridSx, getSectionHeaderStyle, gridColumnHeadersStyle, leadingColumnsPlaceholderStyle, weekHeaderCardStyle } from "../MasterSchedule/v2/utils";
import { SynergyStaffingRow } from "./SynergyStaffingRow";
import { type SynergyGridData, topFiltersBarHeight } from "./utils";

type Props = {
	gridData: SynergyGridData;
	weekStartDate: DateTime | undefined;
	collapsedSections: string[];
	handleSectionCollapseToggle: (section: string) => void;
	recurringShifts?: RecurringShift[];
	refreshRecurringShifts: () => Promise<void>;
	uniqueSections: string[];
	timeOffRequests: TimeOffRequest[];
	refetchDayOffRequests?: () => Promise<void>;
	refetchDayOffRequestsLoading?: boolean;
	selectedNurseId?: number | null;
};

export const SynergyStaffingGrid: FC<Props> = ({
	gridData,
	weekStartDate,
	collapsedSections,
	handleSectionCollapseToggle,
	recurringShifts,
	refreshRecurringShifts,
	uniqueSections,
	timeOffRequests,
	refetchDayOffRequests,
	refetchDayOffRequestsLoading,
	selectedNurseId,
}) => {
	const { t } = useTranslation();

	return (
		<LocationCellProvider>
			<Box sx={getGridSx(1, topFiltersBarHeight, true, false)}>
				{/* Empty placeholder for top-left corner */}
				<Box sx={leadingColumnsPlaceholderStyle} />

				{weekStartDate && (
					<Box sx={[weekHeaderCardStyle, { position: "sticky", top: 0, zIndex: 4 }] as SxProps<Theme>}>
						{formatWeekRangeWithMonth(weekStartDate, weekStartDate.plus({ day: 6 }))}
					</Box>
				)}

				<Box sx={gridColumnHeadersStyle}>{t("Facility")}</Box>

				<Box sx={gridColumnHeadersStyle}>{t("Visits")}</Box>

				{Array.from({ length: 7 }, (_, dayIndex) => {
					const exactDate = weekStartDate?.plus({ day: dayIndex });
					return (
						<Box key={exactDate?.toISODate() ?? dayIndex} sx={gridColumnHeadersStyle}>
							<span>{exactDate?.toFormat("ccc")}</span>
							<Box
								sx={{
									position: "absolute",
									bottom: "50%",
									left: "75%",
									transform: "translate(-50%, 50%)",
								}}
							>
								{exactDate && <span style={{ fontSize: "0.75em", opacity: 0.7 }}>{exactDate.toFormat("M/d")}</span>}
							</Box>
						</Box>
					);
				})}

				{/* Render Sections and Rows */}
				{Object.keys(gridData).length === 0 ? (
					<Box
						sx={{
							gridColumn: "1 / -1",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							padding: "40px",
							color: "#999",
						}}
					>
						<Typography>No data available</Typography>
					</Box>
				) : (
					Object.keys(gridData)
						.sort((a, b) => {
							// "Unassigned" always comes first
							if (a === "Unassigned") return -1;
							if (b === "Unassigned") return 1;
							// Sort other sections alphabetically
							return a.localeCompare(b);
						})
						.map((section) => {
							const locationsInSection = gridData[section];
							const isCollapsed = collapsedSections.includes(section);

							return (
								<Fragment key={section}>
									{/* Section Header */}
									<Box sx={getSectionHeaderStyle(1, true, false)} className="ms2-role-header">
										<Typography
											sx={{
												fontSize: "inherit",
												fontWeight: "inherit",
												color: "inherit",
												lineHeight: "inherit",
											}}
										>
											{section}
										</Typography>
										<IconButton
											onClick={() => handleSectionCollapseToggle(section)}
											sx={{
												fontSize: "inherit",
												p: 0,
											}}
										>
											{isCollapsed ? (
												<ExpandMore
													sx={{
														fontSize: "18px",
													}}
												/>
											) : (
												<ExpandLess
													sx={{
														fontSize: "18px",
													}}
												/>
											)}
										</IconButton>
									</Box>

									{/* Rows for this section */}
									{!isCollapsed &&
										locationsInSection.map((rowData, index) => {
											// Filter recurring shifts for this location
											const shiftsAtLocation = recurringShifts?.filter(
												(shift) =>
													shift.location === rowData.location.location &&
													shift.shiftStart.substring(0, 5) === rowData.location.shift_start.substring(0, 5) &&
													shift.shiftEnd.substring(0, 5) === rowData.location.shift_end.substring(0, 5),
											);

											return (
												<SynergyStaffingRow
													key={`${section}-${rowData.location.id}-${index}`}
													rowData={rowData}
													shiftsAtLocation={shiftsAtLocation}
													refreshRecurringShifts={refreshRecurringShifts}
													uniqueSections={uniqueSections}
													timeOffRequests={timeOffRequests}
													refetchDayOffRequests={refetchDayOffRequests}
													refetchDayOffRequestsLoading={refetchDayOffRequestsLoading}
													selectedNurseId={selectedNurseId}
												/>
											);
										})}
								</Fragment>
							);
						})
				)}
			</Box>
		</LocationCellProvider>
	);
};
